import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import CrudPage from "@/components/CrudPage";
import DocumentsPanel from "@/components/DocumentsPanel";
import { VehiclePhoto, VehicleUploads, VehicleThumb, generateAiPhoto } from "@/components/VehicleMedia";
import { DataTable, FormDialog, InfoGrid, Panel, StatusBadge } from "@/components/shared";
import { api, notifyErr, fmtMoney } from "@/lib/api";
import { VEHICLE_FIELDS, withCargos } from "@/lib/forms";
import { useAuth } from "@/context/AuthContext";
import { AiVehicleFill, AiVehicleBulk } from "@/components/AiVehicleFill";

const COLS = [
  { key: "plate", label: "Targa", render: (r) => <span className="flex items-center gap-2"><VehicleThumb vid={r.id} photoKey={r.photo_id} testid={`vehicle-thumb-${r.id}`} /><span className="font-mono">{r.plate}</span></span> }, { key: "brand", label: "Marca" }, { key: "model", label: "Modello" },
  { key: "category", label: "Categoria" }, { key: "fuel", label: "Alimentazione" }, { key: "location", label: "Sede" },
  { key: "daily_rate", label: "Tariffa", type: "money" }, { key: "status", label: "Stato", type: "status" },
];

const V_LABELS = {
  archive: ["Disattivare il veicolo?", "Il veicolo resta in archivio con documenti, libretto, immagini, manutenzioni, prenotazioni, contratti e storico. Non sarà disponibile per nuovi noleggi e continua a rientrare nel conteggio del piano.", "Disattiva veicolo"],
  restore: ["Riattivare il veicolo?", "Il conteggio dei veicoli del piano non cambia.", "Riattiva veicolo"],
  delete: ["Eliminare il veicolo?", "Il veicolo passa allo stato eliminato e libera un posto nel piano. Documenti e storico restano conservati.", "Elimina veicolo"],
  toggle: "Disattivati",
};

function UsageBadge({ u }) {
  if (!u || u.max_counted_vehicles == null) return null;
  return <span className={`text-sm px-3 h-10 inline-flex items-center rounded-md border ${u.blocked ? "border-amber-500/40 text-amber-300" : "border-zinc-800 text-zinc-400"}`} data-testid="vehicle-usage">
    Veicoli caricati {u.counted_vehicles}/{u.max_counted_vehicles}</span>;
}

const FR_FIELDS = [
  { name: "franchigia_furto_totale", label: "Franchigia furto totale (€)", type: "number" },
  { name: "franchigia_furto_parziale", label: "Franchigia furto parziale (€)", type: "number" },
  { name: "franchigia_danni", label: "Franchigia danni (€)", type: "number" },
  { name: "franchigia_rc", label: "Franchigia RC (€)", type: "number" },
];
const EMPTY = {};

function BulkFranchigie({ onDone }) {
  const [open, setOpen] = useState(false);
  return <>
    <Button variant="outline" className="h-10" onClick={() => setOpen(true)} data-testid="bulk-franchigie-btn"><ShieldCheck size={15} className="mr-1" />Franchigie per tutti</Button>
    <FormDialog open={open} onOpenChange={setOpen} title="Franchigie uguali per tutti i veicoli" fields={FR_FIELDS} initial={EMPTY}
      top={() => <p className="text-sm text-zinc-400" data-testid="bulk-franchigie-info">Gli importi inseriti sostituiscono quelli di tutti i veicoli della flotta. I campi lasciati vuoti non vengono modificati.</p>}
      onSubmit={async (f) => { const r = await api.post("/vehicles/bulk-franchigie", f); toast.success(`Franchigie aggiornate su ${r.data.updated} veicoli`); onDone(); }} />
  </>;
}

export function VehiclesPage() {
  const { state, can } = useAuth();
  const [u, setU] = useState(null);
  const [k, setK] = useState(0);
  const [bulk, setBulk] = useState(null);
  const photo = useRef(null);
  const reload = () => setK((x) => x + 1);
  const onCreated = async (v) => {
    const f = photo.current;
    photo.current = null;
    if (f && f.type.startsWith("image/")) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post(`/vehicles/${v.id}/photo`, fd); toast.success("Foto del veicolo salvata"); reload(); return; } catch (e) { notifyErr(e); }
    }
    if (state.features?.ai && v?.brand && v?.model) generateAiPhoto(v, reload);
  };
  const formTop = state.features?.ai ? (form, setForm) => !form.id && <AiVehicleFill onSingle={(fields, file) => { setForm((s) => ({ ...s, ...fields })); photo.current = file; }}
    onList={(list, files) => setBulk({ list, files })} /> : undefined;
  const loadUsage = () => api.get("/vehicle-usage").then((r) => setU(r.data)).catch(() => setU(null));
  useEffect(() => { loadUsage(); }, []);
  return <>
    <CrudPage key={k} onCreated={onCreated} formTop={formTop} title="Veicoli" subtitle="Archivio flotta con disponibilità calcolata da prenotazioni, noleggi e manutenzioni" endpoint="/vehicles"
    module="vehicles" columns={COLS} fields={VEHICLE_FIELDS} newLabel="Nuovo veicolo" searchPlaceholder="Cerca per targa, marca, modello, sede…"
    rowLink={(r) => `/veicoli/${r.id}`} labels={V_LABELS} canDelete={state.user?.role === "superadmin"} createBlocked={u?.blocked ? u.message : null}
    onChange={loadUsage} headerExtra={<><UsageBadge u={u} />{can("vehicles", "edit") && <BulkFranchigie onDone={reload} />}</>}
    filters={[{ key: "status", placeholder: "Stato", options: ["disponibile", "prenotato", "in_noleggio", "in_manutenzione", "non_disponibile"] },
      { key: "fuel", placeholder: "Alimentazione", options: ["Benzina", "Diesel", "GPL", "Metano", "Ibrida", "Elettrica"] }]} />
    {bulk && <AiVehicleBulk data={bulk} aiPhotos={!!state.features?.ai} onClose={() => setBulk(null)} onDone={() => { loadUsage(); reload(); }} />}
  </>;
}

export const HIST_COLS = {
  bookings: [{ key: "code", label: "Codice", type: "mono" }, { key: "customer_name", label: "Cliente" }, { key: "vehicle_label", label: "Veicolo" },
    { key: "start", label: "Ritiro", type: "datetime" }, { key: "end", label: "Riconsegna", type: "datetime" }, { key: "status", label: "Stato", type: "status" }],
  rentals: [{ key: "code", label: "Codice", type: "mono" }, { key: "customer_name", label: "Cliente" }, { key: "vehicle_label", label: "Veicolo" },
    { key: "planned_start", label: "Ritiro", type: "datetime" }, { key: "planned_end", label: "Riconsegna", type: "datetime" }, { key: "status", label: "Stato", type: "status" }],
  payments: [{ key: "date", label: "Data", type: "date" }, { key: "type", label: "Tipo", type: "status" }, { key: "amount", label: "Importo", type: "money" },
    { key: "method", label: "Metodo" }, { key: "status", label: "Stato", type: "status" }],
  maintenances: [{ key: "type", label: "Tipo", type: "status" }, { key: "start", label: "Inizio", type: "datetime" }, { key: "end", label: "Fine", type: "datetime" },
    { key: "supplier", label: "Fornitore" }, { key: "cost", label: "Costo", type: "money" }, { key: "status", label: "Stato", type: "status" }],
  deadlines: [{ key: "type", label: "Tipo" }, { key: "due_date", label: "Scadenza", type: "date" }, { key: "status", label: "Stato", type: "status" }],
};

export function VehicleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can, state } = useAuth();
  const [v, setV] = useState(null);
  const [rel, setRel] = useState(null);
  const [edit, setEdit] = useState(false);
  const [docKey, setDocKey] = useState(0);
  const load = () => {
    api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(notifyErr);
    api.get(`/vehicles/${id}/related`).then((r) => setRel(r.data)).catch(notifyErr);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [id]);
  if (!v) return <div className="text-zinc-500">Caricamento…</div>;
  return (
    <div className="space-y-6">
      <button onClick={() => nav("/veicoli")} className="text-sm text-zinc-400 flex items-center gap-1 hover:text-white" data-testid="back-btn"><ArrowLeft size={14} />Veicoli</button>
      <div className="flex flex-col md:flex-row gap-6 fade-up">
        <div className="w-full md:w-80 aspect-[4/3] rounded-xl bg-zinc-900 border border-zinc-800 overflow-hidden grid place-items-center">
          <VehiclePhoto v={v} className={`w-full h-full ${v.photo_style ? "object-contain p-3" : "object-cover"}`} fallback={<span className="font-mono text-2xl text-zinc-600">{v.plate}</span>} />
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-mono text-sm text-brand">{v.plate}</div>
              <h1 className="text-4xl font-bold text-white" data-testid="vehicle-title">{v.brand} {v.model}</h1>
              <div className="mt-2 flex gap-2"><StatusBadge value={v.status} testid="vehicle-status" />{v.record_status === "disabled" && <StatusBadge text="Disattivato" testid="vehicle-disabled-badge" />}</div>
              {can("vehicles", "edit") && <label className="mt-3 flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <Switch checked={!v.calendar_hidden} onCheckedChange={async (on) => { try { await api.put(`/vehicles/${id}`, { calendar_hidden: !on }); toast.success(on ? "Veicolo attivato nel calendario" : "Veicolo nascosto dal calendario"); load(); } catch (e) { notifyErr(e); } }} data-testid="vehicle-calendar-toggle" />
                {v.calendar_hidden ? "Disattivato nel calendario" : "Attivo nel calendario"}</label>}
            </div>
            {can("vehicles", "edit") && <div className="flex flex-wrap gap-2"><VehicleUploads v={v} ai={state.features?.ai} onDone={() => { load(); setDocKey((k) => k + 1); }} />
              <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-vehicle-btn"><Pencil size={15} className="mr-1" />Modifica</Button></div>}
          </div>
          <div className="mt-6"><InfoGrid items={[["Categoria", v.category], ["Anno", v.year], ["Colore", v.color], ["Alimentazione", v.fuel], ["Cambio", v.transmission],
            ["Chilometraggio", v.mileage?.toLocaleString("it-IT")], ["Posti", v.seats], ["Sede", v.location], ["Tariffa", fmtMoney(v.daily_rate)],
            ["Franchigia furto totale", v.franchigia_furto_totale != null && v.franchigia_furto_totale !== "" ? fmtMoney(v.franchigia_furto_totale) : null],
            ["Franchigia furto parziale", v.franchigia_furto_parziale != null && v.franchigia_furto_parziale !== "" ? fmtMoney(v.franchigia_furto_parziale) : null],
            ["Franchigia danni", v.franchigia_danni != null && v.franchigia_danni !== "" ? fmtMoney(v.franchigia_danni) : null],
            ["Franchigia RC", v.franchigia_rc != null && v.franchigia_rc !== "" ? fmtMoney(v.franchigia_rc) : null],
            ["GPS", v.gps ? "Sì" : "No"], ["Blocco motore", v.engine_block ? "Sì" : "No"], ["Note", v.notes]]} /></div>
        </div>
      </div>
      <Tabs defaultValue="libretto">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="libretto" data-testid="tab-libretto">Libretto di circolazione</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Prenotazioni</TabsTrigger>
          <TabsTrigger value="rentals" data-testid="tab-rentals">Noleggi</TabsTrigger>
          <TabsTrigger value="maintenances" data-testid="tab-maintenances">Manutenzioni</TabsTrigger>
          <TabsTrigger value="deadlines" data-testid="tab-deadlines">Scadenze</TabsTrigger>
        </TabsList>
        <TabsContent value="libretto">
          <Panel><p className="text-xs text-zinc-500 mb-4">Carica il libretto con il pulsante “Carica libretto (PDF)”: un solo file PDF, che sostituisce il precedente. L'originale non viene mai modificato; se è configurato il timbro aziendale viene creata automaticamente una copia timbrata, allegata a ogni contratto stampato.</p>
            <DocumentsPanel key={docKey} entityType="vehicle" entityId={id} category="libretto" allowStamp noUpload /></Panel>
        </TabsContent>
        {["bookings", "rentals", "maintenances", "deadlines"].map((k) => (
          <TabsContent key={k} value={k}>
            <DataTable columns={HIST_COLS[k]} rows={rel ? rel[k] : null} testid={`vehicle-${k}-table`}
              onRowClick={k === "rentals" ? (r) => nav(`/noleggi/${r.id}`) : k === "bookings" ? (r) => nav(`/prenotazioni?id=${r.id}`) : null} />
          </TabsContent>
        ))}
      </Tabs>
      <FormDialog open={edit} onOpenChange={setEdit} title="Modifica veicolo" fields={withCargos(VEHICLE_FIELDS, state.features?.cargos)} initial={v}
        onSubmit={async (f) => { await api.put(`/vehicles/${id}`, f); toast.success("Veicolo aggiornato"); load(); }} />
    </div>
  );
}
